import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Folder, ArrowLeft, ExternalLink, Calendar, 
  MessageSquare, Trash2, Search, Loader2 
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const UserProjectInspector = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('username, full_name')
          .eq('id', userId)
          .single();
        
        setUserProfile(profile);

        // Fetch projects for this user
        const { data: projs, error } = await supabase
          .from('projects')
          .select('*')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        setProjects(projs || []);
      } catch (err) {
        toast.error("Failed to load user projects.");
        navigate('/admin/users');
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchData();
  }, [userId, navigate]);

  const filtered = projects.filter(p => 
    p.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-4">
        <Link to="/admin/users">
          <Button variant="ghost" size="icon" className="rounded-xl border border-sonas-border">
            <ArrowLeft size={18} />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-syne font-bold">
            Inspecting: {userProfile?.full_name || 'Architect'}
          </h1>
          <p className="text-sonas-muted text-xs">Viewing all project nodes belonging to @{userProfile?.username}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sonas-dim" />
          <Input 
            placeholder="Search projects..." 
            className="pl-10 h-11 bg-sonas-surface2 border-sonas-border rounded-xl"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">
          {projects.length} Total Nodes
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full py-20 text-center">
            <Loader2 className="animate-spin mx-auto text-sonas-accent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full py-20 text-center opacity-30">
            <Folder size={48} className="mx-auto mb-4" />
            <p className="text-sm font-bold uppercase tracking-widest">No Projects Found</p>
          </div>
        ) : filtered.map(proj => (
          <div key={proj.id} className="bg-sonas-surface border border-sonas-border p-6 rounded-3xl hover:border-sonas-accent/20 transition-all group">
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-xl bg-sonas-accent/5 flex items-center justify-center border border-sonas-accent/10">
                  <Folder size={20} className="text-sonas-accent" />
                </div>
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[9px] font-bold uppercase">
                  {proj.status || 'Draft'}
                </div>
              </div>

              <div>
                <h3 className="font-syne font-bold text-lg truncate">{proj.title}</h3>
                <div className="flex items-center gap-4 mt-2 text-[10px] text-sonas-dim font-medium uppercase tracking-tighter">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(proj.updated_at).toLocaleDateString()}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={12} /> {proj.share_id ? 'Public' : 'Private'}</span>
                </div>
              </div>

              <div className="pt-2">
                <Button 
                  onClick={() => navigate(`/app/chat/${proj.id}`)}
                  className="w-full h-10 bg-sonas-accent/10 hover:bg-sonas-accent text-sonas-accent hover:text-sonas-bg font-bold rounded-xl gap-2 transition-all"
                >
                  <ExternalLink size={14} /> Jump Into Workspace
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserProjectInspector;